import React, { useEffect, useRef, forwardRef } from 'react';
import { StyledSlider, StyledOuterContainer, StyledTextContent, StyledLabel, StyledAppendix, StyledOutput, StyledMinMax, StyledFormMessage, StyledOutputContainer, StyledTrack, StyledSliderContainer, StyledControlContainer, StyledDisplayContainer, StyledIconButtonWrapper } from './index.styles.js';
import {} from '@oneaudi/unified-web-alpha-common';
import { IconButton } from '../icon-button/index.js';
import { pickDataProps } from '../../utils/pick-data-props.js';
export const Slider = forwardRef(({ id, value, label, appendix, hint, error, disabled, invalid, onChange, className, step = 1, max, min, hiddenValues, rangeSeparator = 'to', buttonControl, 'aria-describedby': ariaDescribedBy, 'aria-label': ariaLabel, valueFormat, margin, hiddenMinMax, ...rest }, ref) => {
    const containerRef = useRef(null);
    const outputRef = useRef(null);
    const valueIsArray = Array.isArray(value);
    const getFormattedValue = (value) => {
        return valueFormat ? valueFormat(value) : value.toString();
    };
    const sortedValues = valueIsArray && value.length === 2
        ? [...value].sort((a, b) => a - b)
        : null;
    useEffect(() => {
        if (containerRef.current) {
            if (valueIsArray) {
                containerRef.current.style.setProperty('--value0', value[0].toString());
                containerRef.current.style.setProperty('--value1', value[1].toString());
                containerRef.current.style.setProperty('--start', `var(${value[0] < value[1] ? '--position0' : '--position1'})`);
                containerRef.current.style.setProperty('--end', `var(${value[0] > value[1] ? '--position0' : '--position1'})`);
            }
            else {
                containerRef.current.style.setProperty('--value0', value.toString());
            }
            containerRef.current.style.setProperty('--min', min.toString());
            containerRef.current.style.setProperty('--max', max.toString());
        }
    }, [value, valueIsArray, min, max]);
    const injectOutputSize = () => {
        if (containerRef.current && outputRef.current) {
            containerRef.current.style.setProperty('--output-size', `${outputRef.current.clientWidth}px`);
        }
    };
    useEffect(() => {
        if (document.fonts) {
            document.fonts.ready.then(() => {
                injectOutputSize();
            });
        }
    }, [value]);
    const handleMouseEnter = () => {
        injectOutputSize();
    };
    const handleMouseLeave = () => {
        injectOutputSize();
    };
    let newValue;
    const handleChange = (event, thumbIndex) => {
        const nextValue = Number(event.target.value);
        if (valueIsArray) {
            newValue =
                thumbIndex === 0 ? [nextValue, value[1]] : [value[0], nextValue];
        }
        else {
            newValue = nextValue;
        }
        if (onChange) {
            onChange(newValue, event);
        }
    };
    const decreaseValue = (thumbIndex) => {
        let newValue;
        if (valueIsArray) {
            newValue =
                thumbIndex === 0
                    ? [Math.max(value[0] - step, min), value[1]]
                    : [value[0], Math.max(value[1] - step, min)];
        }
        else {
            newValue = Math.max(value - step, min);
        }
        if (onChange) {
            const event = new Event('input', {
                bubbles: true
            });
            onChange(newValue, event);
        }
    };
    const increaseValue = (thumbIndex) => {
        let newValue;
        if (valueIsArray) {
            newValue =
                thumbIndex === 0
                    ? [Math.min(value[0] + step, max), value[1]]
                    : [value[0], Math.min(value[1] + step, max)];
        }
        else {
            newValue = Math.min(value + step, max);
        }
        if (onChange) {
            const event = new Event('input', {
                bubbles: true
            });
            onChange(newValue, event);
        }
    };
    const sliderLabel = `${id}__label`;
    const renderButtonControl = (_thumbIndex, ariaLabel, icon, onClick) => (React.createElement(IconButton, { size: "md", variant: "tertiary", icon: icon, onClick: onClick, disabled: disabled, "aria-label": ariaLabel }));
    return (React.createElement(StyledOuterContainer, { ref: containerRef, valueIsArray: valueIsArray, margin: margin, className: className, ...pickDataProps(rest) },
        label && (React.createElement(StyledTextContent, null,
            React.createElement(StyledLabel, { id: sliderLabel, disabled: disabled }, label),
            React.createElement(StyledAppendix, { disabled: disabled }, appendix))),
        React.createElement(StyledControlContainer, null,
            buttonControl && (React.createElement(StyledIconButtonWrapper, null,
                valueIsArray &&
                    renderButtonControl(1, buttonControl.defaultSliderAriaLabel.decrease, 'add-circle', () => increaseValue(0)),
                renderButtonControl(0, buttonControl.additionalSliderAriaLabel.decrease, 'remove-circle', () => decreaseValue(0)))),
            React.createElement(StyledDisplayContainer, { valueIsArray: valueIsArray },
                !hiddenValues && (React.createElement(StyledOutputContainer, { valueIsArray: valueIsArray },
                    React.createElement(StyledOutput, { valueIsArray: valueIsArray, ref: outputRef, disabled: disabled, invalid: invalid, rangeSeparator: rangeSeparator }, valueIsArray && sortedValues ? (React.createElement(React.Fragment, null,
                        sortedValues[0] != null &&
                            getFormattedValue(sortedValues[0]),
                        sortedValues[1] != null && (React.createElement(React.Fragment, null,
                            React.createElement("span", null,
                                " ",
                                rangeSeparator,
                                " "),
                            getFormattedValue(sortedValues[1]))))) : (!valueIsArray && getFormattedValue(value))))),
                React.createElement(StyledSliderContainer, { valueIsArray: valueIsArray },
                    React.createElement(StyledTrack, { valueIsArray: valueIsArray, disabled: disabled, invalid: invalid }),
                    React.createElement(StyledSlider, { id: `${id}-0`, ref: ref, type: "range", min: min, max: max, step: step, value: valueIsArray ? value[0] : value, onChange: (e) => handleChange(e, 0), disabled: disabled, invalid: invalid, "aria-valuemin": min, "aria-valuemax": max, "aria-valuenow": valueIsArray ? value[0] : value, "aria-valuetext": getFormattedValue(valueIsArray ? value[0] : value), "aria-describedby": ariaDescribedBy, "aria-labelledby": sliderLabel, "aria-label": ariaLabel, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, valueIsArray: valueIsArray }),
                    valueIsArray && value[1] !== undefined && (React.createElement(StyledSlider, { id: `${id}-1`, ref: ref, type: "range", min: min, max: max, step: step, value: value[1], onChange: (e) => handleChange(e, 1), disabled: disabled, invalid: invalid, role: "slider", "aria-valuemin": min, "aria-valuemax": max, "aria-valuenow": value[1], "aria-valuetext": getFormattedValue(value[1]), "aria-describedby": ariaDescribedBy, "aria-labelledby": sliderLabel, "aria-label": ariaLabel, valueIsArray: valueIsArray }))),
                !hiddenMinMax && (React.createElement(StyledMinMax, { disabled: disabled },
                    React.createElement("span", null, getFormattedValue(min)),
                    React.createElement("span", null, getFormattedValue(max))))),
            buttonControl && (React.createElement(StyledIconButtonWrapper, null, !valueIsArray ? (renderButtonControl(0, buttonControl.defaultSliderAriaLabel.increase, 'add-circle', () => increaseValue(0))) : (React.createElement(React.Fragment, null,
                renderButtonControl(1, buttonControl.additionalSliderAriaLabel.increase, 'add-circle', () => increaseValue(1)),
                renderButtonControl(0, buttonControl.additionalSliderAriaLabel.decrease, 'remove-circle', () => decreaseValue(1))))))),
        hint && (React.createElement(StyledFormMessage, { disabled: disabled, text: hint, variant: "hint", id: "messageID" })),
        invalid && !disabled && (React.createElement(StyledFormMessage, { disabled: disabled, text: error, variant: "error", id: "errorID" }))));
});
Slider.displayName = 'Slider';
//# sourceMappingURL=index.js.map